import { useMemo } from 'react';
import { useUserContext } from '@/hooks/useUserContext';
import { useCompany } from '@/hooks/use-company';
import { useFeedbackConfig } from '@/contexts/FeedbackContext';

const FALLBACK_ACCOUNT_TOKEN = '8ysj675gwbvabokyqa55bxlm9cd4f8d6';
const FALLBACK_API_HOST = 'https://api.sentiment.accessacloud.com';
const FALLBACK_RE_DISPLAY_AFTER_DAYS = '30';
const PRODUCT_NAME = 'ERP Credit Control';

/**
 * Custom hook to build the feedback collector parameters for EvoMfeApp.
 *
 * Both the account token and API host are sourced from FeedbackContext
 * (provided via PidgetSettings), with env var and hardcoded fallbacks.
 *
 * @param feature - The feature name to collect feedback for (e.g., "Disputes", "Dashboard")
 * @returns The parameters object for EvoMfeApp, or null if required context is unavailable
 */
export function useFeedbackCollectorParams(
  feature: string
): Record<string, unknown> | null {
  const { data: userContext } = useUserContext();
  const { selectedCompany } = useCompany();
  const { accountToken: contextToken, apiHost: contextApiHost, reDisplayAfterDays: contextReDisplayAfterDays } = useFeedbackConfig();

  const accountToken =
    contextToken ||
    import.meta.env.VITE_FEEDBACK_COLLECTOR_ACCOUNT_TOKEN ||
    FALLBACK_ACCOUNT_TOKEN;

  const apiHost = contextApiHost || FALLBACK_API_HOST;

  const reDisplayAfterDays = contextReDisplayAfterDays || FALLBACK_RE_DISPLAY_AFTER_DAYS;

  return useMemo(() => {
    if (!accountToken) return null;

    return {
      product: PRODUCT_NAME,
      feature: feature,
      accountToken: accountToken,
      apiHost: apiHost,
      customerName: selectedCompany?.name || 'Unknown',
      userId: userContext?.userId || 'Unknown',
      reDisplayAfterDays: reDisplayAfterDays,
    };
  }, [feature, accountToken, apiHost, reDisplayAfterDays, selectedCompany?.name, userContext?.userId]);
}
